import { useState } from 'react';
import {
  useCreateOAuthClient, useDeleteOAuthClient, useOAuthClients, useRotateOAuthSecret,
  type OAuthClientView,
} from '../api/hooks';
import { useT } from '../i18n/LangProvider';
import { Query } from '../ui/atoms';
import { Icon } from '../ui/Icon';
import { useConfirm } from '../ui/confirm';

const ALL_SCOPES = ['openid', 'profile', 'email', 'roles', 'offline_access'];

/** Shows a freshly-minted secret once — it cannot be retrieved again. */
function SecretReveal({ clientId, secret, onClose }: { clientId: string; secret: string | null; onClose: () => void }) {
  const t = useT();
  return (
    <div className="card" style={{ padding: 16, marginBottom: 14, border: '1px solid var(--ok)', background: 'var(--ok-soft)' }}>
      <div className="spaced" style={{ marginBottom: 8 }}>
        <strong>{t('App registrata — salva queste credenziali ora')}</strong>
        <button className="icon-btn" onClick={onClose}><Icon name="x" size={13} /></button>
      </div>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
        {t('Il client secret viene mostrato')} <strong>{t('una sola volta')}</strong>. {t('Conservalo in un posto sicuro.')}
      </div>
      <Field label="client_id" value={clientId} />
      {secret
        ? <Field label="client_secret" value={secret} />
        : <div className="muted" style={{ fontSize: 12.5 }}>{t('App pubblica (solo PKCE): nessun secret.')}</div>}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginBottom: 8 }}>
      <div className="field-label">{label}</div>
      <div className="row" style={{ gap: 6 }}>
        <code style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 12.5, background: 'var(--bg-elev)', padding: '7px 10px', borderRadius: 7, border: '1px solid var(--line)', wordBreak: 'break-all' }}>{value}</code>
        <button className="btn btn--sm" onClick={() => { void navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }}>
          <Icon name={copied ? 'check' : 'copy'} size={12} />
        </button>
      </div>
    </div>
  );
}

export function OAuthClientsSection() {
  const t = useT();
  const confirm = useConfirm();
  const clientsQ = useOAuthClients();
  const create = useCreateOAuthClient();
  const rotate = useRotateOAuthSecret();
  const remove = useDeleteOAuthClient();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [redirects, setRedirects] = useState('');
  const [confidential, setConfidential] = useState(true);
  const [trusted, setTrusted] = useState(true);
  const [scopes, setScopes] = useState<string[]>(['openid', 'profile', 'email', 'roles']);
  const [error, setError] = useState<string | null>(null);
  const [reveal, setReveal] = useState<{ clientId: string; secret: string | null } | null>(null);

  async function submit() {
    setError(null);
    const redirectUris = redirects.split(/\n|,/).map((s) => s.trim()).filter(Boolean);
    if (!name.trim() || redirectUris.length === 0) {
      setError(t('Nome e almeno una redirect URI sono obbligatori.'));
      return;
    }
    try {
      const c = await create.mutateAsync({ name: name.trim(), redirectUris, allowedScopes: scopes, confidential, trusted });
      setReveal({ clientId: c.clientId, secret: c.clientSecret });
      setShowForm(false);
      setName(''); setRedirects('');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Creazione non riuscita.'));
    }
  }

  async function doRotate(c: OAuthClientView) {
    if (!(await confirm({
      message: t('Rigenerare il secret di “{name}”? Il valore attuale smetterà di funzionare.', { name: c.name }),
      confirmLabel: t('Rigenera'), danger: true,
    }))) return;
    const res = await rotate.mutateAsync(c.id);
    setReveal({ clientId: c.clientId, secret: res.clientSecret });
  }

  return (
    <section style={{ marginBottom: 26 }}>
      <div className="spaced" style={{ marginBottom: 10 }}>
        <div>
          <h2 className="h2" style={{ fontSize: 15 }}>{t('App collegate · Single Sign-On')}</h2>
          <div className="muted" style={{ fontSize: 12.5 }}>
            {t('App che possono far accedere le persone con l’account Touristation (“Accedi con Touristation”).')}
          </div>
        </div>
        <button className="btn btn--brand btn--sm" onClick={() => { setShowForm((s) => !s); setError(null); }}>
          <Icon name="plus" size={13} /> {t('Registra app')}
        </button>
      </div>

      {reveal && <SecretReveal clientId={reveal.clientId} secret={reveal.secret} onClose={() => setReveal(null)} />}

      {showForm && (
        <div className="card" style={{ padding: 16, marginBottom: 14 }}>
          {error && (
            <div style={{ background: '#fdecec', border: '1px solid #f3b6b6', color: '#8f2020', borderRadius: 8, padding: '8px 11px', marginBottom: 12, fontSize: 13 }}>
              <Icon name="warn" size={13} /> {error}
            </div>
          )}
          <div className="row" style={{ gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label className="field-label">{t('Nome app')}</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('Es. Portale prenotazioni')} />
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label className="field-label">{t('Redirect URI (una per riga)')}</label>
            <textarea className="input" rows={2} value={redirects} onChange={(e) => setRedirects(e.target.value)}
              placeholder="https://altrosito.it/auth/callback" />
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{t('Deve coincidere esattamente con l’URL a cui l’app riceve il codice.')}</div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label className="field-label">{t('Dati condivisi (scope)')}</label>
            <div className="cluster" style={{ gap: 6, flexWrap: 'wrap' }}>
              {ALL_SCOPES.map((s) => (
                <button key={s} type="button"
                  className={`chip ${scopes.includes(s) ? 'is-active' : ''}`}
                  onClick={() => setScopes((xs) => xs.includes(s) ? xs.filter((x) => x !== s) : [...xs, s])}
                  disabled={s === 'openid'}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="cluster" style={{ gap: 18, marginBottom: 12 }}>
            <label className="row" style={{ gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={confidential} onChange={(e) => setConfidential(e.target.checked)} />
              {t('App con backend (client secret)')}
            </label>
            <label className="row" style={{ gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={trusted} onChange={(e) => setTrusted(e.target.checked)} />
              {t('App interna (salta la schermata di consenso)')}
            </label>
          </div>
          <div className="cluster" style={{ justifyContent: 'flex-end' }}>
            <button className="btn btn--sm" onClick={() => setShowForm(false)}>{t('Annulla')}</button>
            <button className="btn btn--sm btn--brand" onClick={() => void submit()} disabled={create.isPending}>{t('Registra')}</button>
          </div>
        </div>
      )}

      <Query query={clientsQ} empty={{ test: (d) => d.length === 0, title: t('Nessuna app collegata'), hint: t('Registra la prima app che userà il login Touristation.') }}>
        {(clients) => (
          <div className="col" style={{ gap: 10 }}>
            {clients.map((c) => (
              <div key={c.id} className="card" style={{ padding: 14 }}>
                <div className="spaced">
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>
                      {c.name}
                      {c.trusted && <span className="lrn-chips__n">{t('interna')}</span>}
                      {c.confidential ? <span className="lrn-chips__n">{t('con secret')}</span> : <span className="lrn-chips__n">PKCE</span>}
                    </div>
                    <div className="muted" style={{ fontSize: 12, fontFamily: 'var(--font-mono)' }}>{c.clientId}</div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 4, wordBreak: 'break-all' }}>
                      ↩ {c.redirectUris.join(' · ')}
                    </div>
                    <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>scope: {c.allowedScopes.join(' ')}</div>
                  </div>
                  <div className="row" style={{ gap: 4, flexShrink: 0 }}>
                    {c.confidential && (
                      <button className="icon-btn" title={t('Rigenera secret')} onClick={() => void doRotate(c)}><Icon name="refresh" size={13} /></button>
                    )}
                    <button className="icon-btn icon-btn--danger" title={t('Elimina')}
                      onClick={async () => { if (await confirm({ message: t('Eliminare l’app “{name}”? Smetterà di poter far accedere gli utenti.', { name: c.name }), confirmLabel: t('Elimina'), danger: true })) remove.mutate(c.id); }}>
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Query>
    </section>
  );
}
